<template>
  <div class="flex flex-col md:flex-row gap-4">
    <div class="w-full mt-4">
      <div v-if="form" class="bg-white dark-bg-custom p-4 rounded-md">
        <div v-if="form" class="w-full">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="w-full">
              <v-text-field
                v-model="form.code"
                outlined
                dense
                hint="Should be 6 Characters"
                label="Organization Code *"
                disabled
                hide-details="auto"
              ></v-text-field>
            </div>

            <div class="w-full">
              <v-text-field
                v-model="form.name"
                outlined
                dense
                hide-details="auto"
                label="Organization Name *"
              ></v-text-field>
            </div>

            <div class="w-full">
              <v-text-field
                v-model="form.mobile"
                outlined
                dense
                hide-details="auto"
                label="Organization Mobile Number"
                placeholder="e.g. 9876543210"
                prepend-inner-icon="mdi-phone"
              ></v-text-field>
            </div>
          </div>

          <!-- Address Details Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4">
            <h4 class="text-md font-semibold">Address Details</h4>
          </div>
          <div class="mb-6">
            <CommonAutoComplete
              :addressLine1="form.address_line_1"
              @selected-place="handleAddressSelectedPlace"
            />
          </div>
          <div class="grid grid-cols-1 gap-6 mb-6">
            <v-text-field
              v-model="form.address_line_1"
              outlined
              dense
              hide-details="auto"
              label="Address Line 1 *"
            ></v-text-field>
            <v-text-field
              v-model="form.address_line_2"
              outlined
              dense
              hide-details="auto"
              label="Address Line 2"
            ></v-text-field>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <v-text-field
              v-model="form.city"
              outlined
              dense
              hide-details="auto"
              label="City *"
            ></v-text-field>
            <v-select
              v-model="form.state"
              :items="states"
              item-text="name"
              item-value="name"
              outlined
              dense
              hide-details="auto"
              label="State *"
            ></v-select>
            <v-text-field
              v-model="form.pincode"
              outlined
              dense
              hide-details="auto"
              label="Pincode *"
              type="number"
            ></v-text-field>
          </div>

          <!-- Domain Verification Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4">
            <h4 class="text-md font-semibold text-left">Domain Setup</h4>
            <p class="text-xs text-slate-500 mt-1 leading-normal text-left">
              Register and verify your custom organization domain to enable sending official system
              emails and transactional communications from your own domain.
            </p>
          </div>

          <!-- Loading state -->
          <div v-if="domainLoading" class="py-12 text-center">
            <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
            <div class="text-slate-400 mt-2 font-medium text-xs">
              Fetching domain configuration...
            </div>
          </div>

          <div v-else class="w-full mb-6">
            <!-- Case 1: Domain is configured -->
            <div v-if="domainConfig.configured">
              <!-- Verification status banner -->
              <div
                :class="[
                  'rounded-xl p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6',
                  domainConfig.fullyVerified
                    ? 'bg-emerald-50 border-emerald-100 text-emerald-950'
                    : 'bg-amber-50 border-amber-100 text-amber-950'
                ]"
              >
                <div class="flex items-center gap-3">
                  <v-avatar
                    :color="domainConfig.fullyVerified ? 'green lighten-5' : 'amber lighten-5'"
                    size="40"
                  >
                    <v-icon
                      :color="domainConfig.fullyVerified ? 'green darken-2' : 'amber darken-2'"
                    >
                      {{
                        domainConfig.fullyVerified
                          ? 'mdi-check-decagram-outline'
                          : 'mdi-clock-outline'
                      }}
                    </v-icon>
                  </v-avatar>
                  <div class="flex flex-col text-left">
                    <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Verification Status
                    </span>
                    <span
                      class="text-sm font-bold mt-0.5"
                      :class="domainConfig.fullyVerified ? 'text-green-700' : 'text-amber-700'"
                    >
                      {{
                        domainConfig.fullyVerified ? 'Verified & Active' : 'Verification Pending'
                      }}
                    </span>
                  </div>
                </div>

                <div class="flex justify-end items-center gap-2">
                  <v-btn
                    color="indigo"
                    outlined
                    class="normal-case rounded-lg text-xs"
                    small
                    @click="getDomainStatus"
                    :loading="domainActionLoading"
                  >
                    <v-icon left size="14">mdi-refresh</v-icon>
                    Check Status
                  </v-btn>
                  <v-btn
                    color="red"
                    outlined
                    class="normal-case rounded-lg text-xs"
                    small
                    @click="removeDomain"
                    :loading="domainActionLoading"
                  >
                    <v-icon left size="14">mdi-delete-outline</v-icon>
                    Remove Domain
                  </v-btn>
                </div>
              </div>

              <!-- Domain details -->
              <div class="bg-slate-50 rounded-xl border border-slate-100 p-5 mb-6 text-left">
                <h4 class="text-xs font-bold text-slate-800 border-b border-slate-100 pb-3 mb-4">
                  Registered Domain Details
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 text-xs">
                  <div class="flex flex-col gap-1">
                    <span class="font-semibold text-slate-400">Configured Domain</span>
                    <span class="text-sm font-bold text-slate-800">{{ domainConfig.domain }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="font-semibold text-slate-400">SES Identity Status</span>
                    <span class="text-slate-700 font-semibold">{{
                      domainConfig.domainStatus
                    }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="font-semibold text-slate-400">DKIM Authentication Status</span>
                    <span class="text-slate-700 font-semibold">{{ domainConfig.dkimStatus }}</span>
                  </div>
                  <div class="flex flex-col gap-1" v-if="domainConfig.verificationRequestedAt">
                    <span class="font-semibold text-slate-400">Requested Verification At</span>
                    <span class="text-slate-700 font-medium">
                      {{ formatDateTime(domainConfig.verificationRequestedAt) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Instructions / Notes -->
              <v-alert
                type="info"
                outlined
                prominent
                border="left"
                class="rounded-xl text-xs mb-6 text-left"
                color="indigo"
                icon="mdi-information-outline"
              >
                {{ domainConfig.note }}
              </v-alert>

              <!-- DNS Records Table -->
              <div
                class="mb-6 text-left"
                v-if="domainConfig.dnsRecords && domainConfig.dnsRecords.length"
              >
                <h3 class="text-sm font-bold text-slate-800 mb-3">Required DNS Records</h3>
                <p class="text-xs text-slate-500 mb-4">
                  Add the following records to your domain's DNS settings (with your registrar like
                  GoDaddy, Cloudflare, Namecheap, etc.) to complete verification.
                </p>

                <v-simple-table dense class="border rounded-xl">
                  <template v-slot:default>
                    <thead>
                      <tr class="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                        <th class="py-3 px-4">Type</th>
                        <th class="py-3 px-4">Host / Name</th>
                        <th class="py-3 px-4">Value / Target</th>
                        <th class="py-3 px-4">Purpose</th>
                        <th class="py-3 px-4">Priority</th>
                      </tr>
                    </thead>
                    <tbody class="text-xs">
                      <tr
                        v-for="(record, index) in domainConfig.dnsRecords"
                        :key="index"
                        class="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td class="py-3 px-4 font-mono font-bold text-slate-600">
                          <v-chip x-small color="slate lighten-3" class="font-bold">{{
                            record.type
                          }}</v-chip>
                        </td>
                        <td class="py-3 px-4">
                          <div class="flex items-center gap-2">
                            <span class="font-mono text-slate-700 truncate max-w-xs">{{
                              record.name
                            }}</span>
                            <v-btn icon x-small @click="copyToClipboard(record.name)">
                              <v-icon size="14">mdi-content-copy</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td class="py-3 px-4">
                          <div class="flex items-center gap-2">
                            <span class="font-mono text-slate-700 truncate max-w-xs">{{
                              record.value
                            }}</span>
                            <v-btn icon x-small @click="copyToClipboard(record.value)">
                              <v-icon size="14">mdi-content-copy</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td class="py-3 px-4 text-slate-500">{{ record.purpose }}</td>
                        <td class="py-3 px-4">
                          <v-chip
                            x-small
                            :color="
                              record.priority === 'High'
                                ? 'red lighten-5 text-red-700'
                                : 'amber lighten-5 text-amber-700'
                            "
                            class="font-bold"
                          >
                            {{ record.priority }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <!-- Instructions Section -->
              <div class="mb-6 text-left" v-if="!domainConfig.fullyVerified">
                <h3 class="text-sm font-bold text-slate-800 mb-3">Verification Instructions</h3>
                <div class="bg-indigo-50/50 rounded-xl border border-indigo-100 p-5">
                  <div class="flex flex-col gap-3">
                    <div
                      v-for="(instructionText, stepKey) in domainConfig.instructions ||
                      defaultInstructions"
                      :key="stepKey"
                      class="flex items-start gap-3 text-xs"
                    >
                      <v-avatar color="indigo" class="white--text font-bold" size="20">
                        {{ stepKey.replace('step', '') }}
                      </v-avatar>
                      <div class="text-slate-700 font-medium pt-0.5">
                        {{ instructionText }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Case 2: No domain configured -->
            <div v-else>
              <v-alert
                type="warning"
                outlined
                class="rounded-xl text-xs text-left"
                color="amber darken-3"
                icon="mdi-alert-circle-outline"
              >
                No custom domain configured for sending emails. Please enter your domain name below
                to start the verification process.
              </v-alert>

              <v-card class="border border-slate-100 rounded-xl bg-slate-50" flat>
                <v-card-text class="p-5 flex flex-col gap-4 text-left">
                  <div class="text-xs font-bold text-slate-800">Register Domain Name</div>

                  <div class="flex flex-col md:flex-row items-center gap-4">
                    <div class="flex-grow w-full md:max-w-md">
                      <v-text-field
                        v-model="domainName"
                        placeholder="e.g. company.com"
                        outlined
                        dense
                        hide-details="auto"
                        prepend-inner-icon="mdi-web"
                        class="rounded-lg bg-white text-xs"
                      ></v-text-field>
                    </div>

                    <v-btn
                      color="indigo"
                      class="normal-case rounded-lg text-xs white--text"
                      depressed
                      @click="registerDomain"
                      :loading="domainActionLoading"
                      :disabled="!domainName"
                    >
                      <v-icon left size="16">mdi-plus</v-icon>
                      Register & Verify
                    </v-btn>
                  </div>

                  <div class="text-[11px] text-slate-500 leading-normal mt-2">
                    <strong>Note:</strong> AWS SES requires verification to send emails from your
                    domain name. Entering your domain will generate DNS TXT and CNAME records that
                    you must add to your domain registrar's settings.
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Logo Upload Section -->
          <v-divider class="my-6"></v-divider>
          <div class="flex flex-col">
            <h2 class="text-md font-semibold mb-4">Upload Logo</h2>
            <div
              @drop="handleDrop"
              @dragover="handleDragOver"
              @dragenter="handleDragEnter"
              @dragleave="handleDragLeave"
              :class="[
                'border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer logo-upload-container',
                isDragOver
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 hover:border-blue-500'
              ]"
              @click="$refs.fileInputForCropper.click()"
            >
              <input
                type="file"
                ref="fileInputForCropper"
                style="display: none"
                @change="onFileSelectedForCropper"
                accept="image/*"
              />

              <!-- Preview of cropped image or current logo or upload area -->

              <div class="tab-items items-center">
                <div
                  v-if="previewImage"
                  class="logo-preview-container flex items-center justify-between w-full"
                >
                  <div class="logo-wrapper relative">
                    <img :src="previewImage" alt="Logo Preview" class="org-logo" />
                  </div>
                  <div>
                    <v-icon @click.stop="removePreviewImage" class="ml-4 cursor-pointer">
                      mdi-close
                    </v-icon>
                  </div>
                </div>
                <div v-else class="upload-placeholder">
                  <div class="text-4xl mb-3">📁</div>
                  <h3 class="text-lg font-semibold mb-2">Drag & Drop your image file here</h3>
                  <p class="text-gray-600 mb-3">or</p>
                  <button
                    class="px-5 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50 transition-colors"
                  >
                    Browse Files
                  </button>
                </div>
              </div>
            </div>

            <!-- Selected File Display -->
            <div
              v-if="selectedFileName"
              class="flex items-center bg-gray-100 p-3 rounded-lg w-full mt-3"
            >
              <span class="flex-1 text-left truncate">{{ selectedFileName }}</span>
              <button @click.stop="removeFile" class="text-red-500 text-xl ml-3">&times;</button>
            </div>
          </div>

          <!-- Statutory Registrations Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4">
            <h2 class="text-md font-semibold">Statutory Registrations</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="w-full">
              <v-text-field
                v-model="form.statutoryDetails.pan"
                outlined
                dense
                hide-details="auto"
                label="Permanent Account Number (PAN) *"
                :rules="[
                  (v) => !!v || 'PAN is required',
                  (v) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v) || 'Invalid PAN format'
                ]"
              ></v-text-field>
            </div>
            <div class="w-full">
              <v-text-field
                v-model="form.statutoryDetails.tan"
                outlined
                dense
                hide-details="auto"
                label="Tax Deduction Account Number (TAN) *"
                :rules="[
                  (v) => !!v || 'TAN is required',
                  (v) => /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(v) || 'Invalid TAN format'
                ]"
              ></v-text-field>
            </div>
            <div class="w-full">
              <v-text-field
                v-model="form.statutoryDetails.gstNumber"
                outlined
                dense
                hide-details="auto"
                label="GSTIN / GST Number"
                placeholder="e.g. 27AAAAA0000A1Z5"
              ></v-text-field>
            </div>
            <div class="w-full">
              <v-text-field
                v-model="form.statutoryDetails.tracesUserId"
                outlined
                dense
                hide-details="auto"
                label="TRACES Login ID"
              ></v-text-field>
            </div>
          </div>

          <!-- Bank Accounts Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4 flex justify-between items-center">
            <div>
              <h2 class="text-md font-semibold text-slate-800">Bank Accounts</h2>
              <p class="text-xs text-slate-500 mb-0">
                Manage organization bank accounts, UPI details, and individual payment QR codes.
              </p>
            </div>
            <v-btn text color="primary" class="normal-case px-2 font-semibold" @click="addBank">
              <v-icon left small>mdi-plus</v-icon> Add Bank Account
            </v-btn>
          </div>

          <div
            v-for="(bank, index) in form.banks"
            :key="`bank-${index}`"
            class="mb-6 p-5 border border-slate-200 rounded-xl bg-slate-50/70 shadow-sm"
          >
            <!-- Bank Item Header -->
            <div class="flex justify-between items-center mb-4 pb-2 border-b border-slate-200">
              <div class="flex items-center gap-2">
                <v-icon small color="primary">mdi-bank</v-icon>
                <span class="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Bank Account {{ index + 1 }} {{ bank.bankName ? `(${bank.bankName})` : '' }}
                </span>
              </div>
              <v-btn
                v-if="form.banks.length > 1"
                icon
                small
                color="error"
                @click="removeBank(index)"
              >
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <!-- Standard Account Details Fields Grid -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
              <!-- IFSC Code (Trigger for Auto-population) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.bankIfscNumber"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  label="IFSC Code *"
                  placeholder="e.g. HDFC0002377"
                  maxlength="11"
                  :loading="bank.loadingIfsc"
                  append-icon="mdi-magnify"
                  @click:append="lookupIfsc(index)"
                  @click:clear="clearBankDetails(index)"
                  @input="(v) => onIfscInput(index, v)"
                  @keydown.enter.prevent="lookupIfsc(index)"
                ></v-text-field>
              </div>

              <!-- Bank Name (Auto-populated, Disabled) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.bankName"
                  outlined
                  dense
                  disabled
                  background-color="#f8fafc"
                  hide-details="auto"
                  label="Bank Name (Auto-populated) *"
                  placeholder="Auto-filled via IFSC"
                ></v-text-field>
              </div>

              <!-- Branch / Centre (Auto-populated, Disabled) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.branchName"
                  outlined
                  dense
                  disabled
                  background-color="#f8fafc"
                  hide-details="auto"
                  label="Branch / Centre (Auto-populated)"
                  placeholder="Auto-filled via IFSC"
                ></v-text-field>
              </div>

              <!-- Account Number (User Editable) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.bankAccountNumber"
                  outlined
                  dense
                  hide-details="auto"
                  label="Account Number *"
                  placeholder="e.g. 5010023456789"
                  :type="bank.showAccountNumber ? 'text' : 'password'"
                  :append-icon="bank.showAccountNumber ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="$set(bank, 'showAccountNumber', !bank.showAccountNumber)"
                ></v-text-field>
              </div>

              <!-- Confirm Account Number (User Editable) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.confirmBankAccountNumber"
                  outlined
                  dense
                  hide-details="auto"
                  label="Confirm Account Number *"
                  placeholder="Re-enter Account Number"
                  type="text"
                  :rules="[
                    (v) =>
                      !bank.bankAccountNumber ||
                      v === bank.bankAccountNumber ||
                      'Account numbers do not match'
                  ]"
                ></v-text-field>
              </div>

              <!-- Account Holder Name (User Editable) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.accountHolderName"
                  outlined
                  dense
                  hide-details="auto"
                  label="Account Holder Name"
                  placeholder="e.g. FinFi Technologies Pvt Ltd"
                ></v-text-field>
              </div>

              <!-- Branch Address (Auto-populated from IFSC, Disabled) -->
              <div class="w-full md:col-span-2">
                <v-text-field
                  v-model="bank.branchAddress"
                  outlined
                  dense
                  disabled
                  background-color="#f8fafc"
                  hide-details="auto"
                  label="Bank Branch Address (Auto-populated)"
                  placeholder="Auto-filled via IFSC"
                ></v-text-field>
              </div>

              <!-- MICR / BSR Code (Auto-populated / Disabled) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.bsrCode"
                  outlined
                  dense
                  disabled
                  background-color="#f8fafc"
                  hide-details="auto"
                  label="MICR / BSR Code"
                  placeholder="Auto-filled via IFSC"
                ></v-text-field>
              </div>

              <!-- UPI ID / VPA (Enabled only when IFSC supports UPI) -->
              <div class="w-full">
                <v-text-field
                  v-model="bank.upiId"
                  outlined
                  dense
                  :disabled="!bank.upiEnabled"
                  :background-color="!bank.upiEnabled ? '#f8fafc' : undefined"
                  hide-details="auto"
                  :label="bank.upiEnabled ? 'UPI ID / VPA' : 'UPI ID / VPA (UPI Not Supported)'"
                  :placeholder="bank.upiEnabled ? 'e.g. finfi@hdfcbank' : 'UPI not supported for this IFSC'"
                ></v-text-field>
              </div>

              <!-- Tag Office / Branch Dropdown -->
              <div class="w-full md:col-span-2">
                <v-select
                  v-model="bank.branchId"
                  :items="branches"
                  item-text="label"
                  item-value="_id"
                  outlined
                  dense
                  hide-details="auto"
                  label="Tag Office / Branch"
                  clearable
                ></v-select>
              </div>
            </div>

            <!-- Per-Bank QR Code Upload & Preview -->
            <div class="pt-3 border-t border-slate-200">
              <label class="text-xs font-semibold text-slate-700 block mb-2">
                Bank Payment / UPI QR Code
              </label>
              <div class="flex flex-col sm:flex-row items-start gap-4">
                <!-- QR Code Thumbnail -->
                <div
                  v-if="getBankQrDisplayUrl(bank)"
                  class="relative border-2 border-dashed border-slate-300 rounded-xl p-2 bg-white flex flex-col items-center justify-center shadow-sm"
                >
                  <img
                    :src="getBankQrDisplayUrl(bank)"
                    alt="Bank QR Code"
                    class="w-28 h-28 object-contain rounded-lg"
                  />
                  <v-btn
                    x-small
                    color="error"
                    text
                    class="mt-1 text-none font-semibold"
                    @click="removeBankQrCode(index)"
                  >
                    <v-icon x-small left>mdi-delete</v-icon> Remove QR
                  </v-btn>
                </div>

                <!-- QR Upload Action -->
                <div class="flex flex-col gap-1.5">
                  <input
                    type="file"
                    :ref="`bankQrInput_${index}`"
                    style="display: none"
                    accept="image/*"
                    @change="handleBankQrCodeUpload($event, index)"
                  />
                  <v-btn
                    outlined
                    small
                    color="primary"
                    class="text-none font-semibold rounded-lg"
                    :loading="bankQrCodeUploadingIndex === index"
                    @click="triggerBankQrSelect(index)"
                  >
                    <v-icon small left>mdi-qrcode-scan</v-icon>
                    {{ getBankQrDisplayUrl(bank) ? 'Change QR Code' : 'Upload Bank QR Code' }}
                  </v-btn>
                  <span class="text-[11px] text-slate-500">
                    Upload UPI QR image for this specific bank to display on invoices and receipts.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Authorized Signatures Section -->
          <v-divider class="my-6"></v-divider>
          <AuthorizedSignaturesSection
            :signatures="form.authorizedSignatures"
            :get-media-url="getMediaUrl"
            @signatures-updated="onSignaturesUpdated"
            @save="saveOrgTdsDetails"
          />

          <!-- Financial Settings Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4">
            <h2 class="text-md font-semibold">Financial Settings</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="w-full">
              <v-select
                :items="[
                  { text: 'January', value: 0 },
                  { text: 'February', value: 1 },
                  { text: 'March', value: 2 },
                  { text: 'April', value: 3 },
                  { text: 'May', value: 4 },
                  { text: 'June', value: 5 },
                  { text: 'July', value: 6 },
                  { text: 'August', value: 7 },
                  { text: 'September', value: 8 },
                  { text: 'October', value: 9 },
                  { text: 'November', value: 10 },
                  { text: 'December', value: 11 }
                ]"
                v-model="form.financialStartMonth"
                label="Financial Start Month"
                dense
                hide-details="auto"
                outlined
              ></v-select>
            </div>

            <div class="w-full">
              <v-text-field
                type="number"
                v-model="form.reimbursementDate"
                outlined
                dense
                hide-details="auto"
                label="Reimbursement Date (of every month)"
                min="1"
                max="31"
              ></v-text-field>
            </div>

            <div class="w-full">
              <v-select
                :items="[
                  { text: 'Last Working Day of Month', value: 'last_working_day' },
                  { text: 'Specific Date', value: 'specific_date' }
                ]"
                v-model="form.salaryRolloutDate"
                label="Salary Rollout Date"
                dense
                hide-details="auto"
                outlined
              ></v-select>
            </div>

            <div class="w-full" v-if="form.salaryRolloutDate === 'specific_date'">
              <v-text-field
                type="number"
                v-model="form.specificSalaryDate"
                outlined
                dense
                hide-details="auto"
                label="Specific Salary Date"
                min="1"
                max="31"
              ></v-text-field>
            </div>
          </div>

          <!-- Credit Bureau Reporting Settings (Loan Management) Section -->
          <v-divider class="my-6"></v-divider>
          <div class="w-full mb-4">
            <h4 class="text-md font-semibold">
              Credit Bureau Reporting Settings (Loan Management)
            </h4>
            <p class="text-xs text-slate-500 mt-1">
              Configure member codes and short names for credit bureau report submissions. If left
              empty, system defaults will be used.
            </p>
          </div>

          <!-- TransUnion CIBIL -->
          <div class="w-full mb-5">
            <div class="flex items-center gap-2 mb-2">
              <v-icon small color="primary">mdi-file-document-outline</v-icon>
              <h5 class="text-sm font-semibold">TransUnion Consumer Data Report (CIBIL)</h5>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <v-text-field
                v-model="form.creditBureauDetails.transUnion.memberCode"
                outlined
                dense
                hide-details="auto"
                label="Member Code"
                placeholder="e.g. NB06600001"
              ></v-text-field>
              <v-text-field
                v-model="form.creditBureauDetails.transUnion.memberShortName"
                outlined
                dense
                hide-details="auto"
                label="Member Short Name"
                placeholder="e.g. ZAVRON"
              ></v-text-field>
            </div>
          </div>

          <!-- CRIF HighMark -->
          <div class="w-full mb-5">
            <div class="flex items-center gap-2 mb-2">
              <v-icon small color="primary">mdi-file-document-outline</v-icon>
              <h5 class="text-sm font-semibold">CRIF HighMark Consumer Data Report</h5>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <v-text-field
                v-model="form.creditBureauDetails.crif.memberCode"
                outlined
                dense
                hide-details="auto"
                label="Member Code"
                placeholder="e.g. NBF0002536"
              ></v-text-field>
              <v-text-field
                v-model="form.creditBureauDetails.crif.memberShortName"
                outlined
                dense
                hide-details="auto"
                label="Member Short Name"
                placeholder="e.g. ZAVRON"
              ></v-text-field>
            </div>
          </div>

          <!-- Equifax -->
          <div class="w-full mb-5">
            <div class="flex items-center gap-2 mb-2">
              <v-icon small color="primary">mdi-file-document-outline</v-icon>
              <h5 class="text-sm font-semibold">Equifax Consumer Data Report</h5>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <v-text-field
                v-model="form.creditBureauDetails.equifax.memberCode"
                outlined
                dense
                hide-details="auto"
                label="Member Code"
                placeholder="e.g. NB06600001"
              ></v-text-field>
              <v-text-field
                v-model="form.creditBureauDetails.equifax.memberShortName"
                outlined
                dense
                hide-details="auto"
                label="Member Short Name"
                placeholder="e.g. ZAVRON"
              ></v-text-field>
            </div>
          </div>

          <!-- Experian -->
          <div class="w-full mb-5">
            <div class="flex items-center gap-2 mb-2">
              <v-icon small color="primary">mdi-file-document-outline</v-icon>
              <h5 class="text-sm font-semibold">Experian Consumer Data Report</h5>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <v-text-field
                v-model="form.creditBureauDetails.experian.memberCode"
                outlined
                dense
                hide-details="auto"
                label="Member Code"
                placeholder="e.g. NB06600001"
              ></v-text-field>
              <v-text-field
                v-model="form.creditBureauDetails.experian.memberShortName"
                outlined
                dense
                hide-details="auto"
                label="Member Short Name"
                placeholder="e.g. ZAVRON"
              ></v-text-field>
            </div>
          </div>

          <div class="flex justify-end w-full mt-4">
            <v-btn
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text normal-case"
              @click="updateOrg()"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <div class="map-container w-full mt-4 md:h-[770px] rounded-md" v-if="this.branches.length > 0">
      <div><span class="text-md font-semibold">Company/ Branches Locations</span></div>
      <div ref="map" class="w-full h-[700px]"></div>
    </div>

    <!-- Logo Crop Dialog -->
    <v-dialog v-model="logoCropDialog" max-width="800">
      <v-card>
        <div class="flex justify-between items-center p-4">
          <h3 class="form-title">Crop Logo</h3>
          <v-icon @click="closeLogoCropDialog" class="cursor-pointer">mdi-close</v-icon>
        </div>
        <v-divider></v-divider>
        <v-card-text>
          <div>
            <div class="cropper-container mb-4">
              <cropper
                ref="cropper"
                class="cropper-example"
                :src="cropperImage"
                :stencil-props="{
                  aspectRatio: 16 / 9
                }"
                @change="onCropperChange"
              />
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <div class="flex justify-end p-4" v-if="cropperImage">
          <v-btn text class="normal-case" @click="cancelCropper">Cancel</v-btn>
          <v-btn
            :color="$vuetify.theme.currentTheme.primary"
            class="normal-case white--text min-w-24 ml-2"
            @click="applyCroppedImage"
          >
            <v-icon left size="18">mdi-content-save</v-icon> Apply
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global google */

import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { axiosUrl } from '@/utils/workforceApi'
import AuthorizedSignaturesSection from '@/components/Workforce/Organization/AuthorizedSignaturesSection.vue'
import CommonAutoComplete from '@/components/CommonAutoComplete.vue'

export default {
  name: 'OrganizationPage',
  layout: 'Workforce/default',
  components: {
    Cropper,
    AuthorizedSignaturesSection,
    CommonAutoComplete
  },
  data() {
    return {
      user: this.$storage.getUniversal('user'),
      orgCode: this.$storage.getUniversal('organization_code'),
      orgData: null,
      domainName: '',
      domainConfig: {
        configured: false,
        domain: '',
        domainStatus: '',
        dkimStatus: '',
        fullyVerified: false,
        dnsRecords: [],
        verificationRequestedAt: null,
        verifiedAt: null,
        note: '',
        instructions: null
      },
      defaultInstructions: {
        step1: 'Go to your domain registrar (GoDaddy, Namecheap, etc.)',
        step2: 'Navigate to DNS Management section',
        step3: 'Add the DNS records shown above',
        step4: 'Wait 24-72 hours for DNS propagation',
        step5: 'Call the status API to check verification'
      },
      domainLoading: false,
      domainActionLoading: false,
      form: {
        code: '',
        name: '',
        mobile: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        pincode: '',
        financialStartMonth: 3, // April (0-indexed, so 3 = April)
        reimbursementDate: 15, // 15th of every month
        salaryRolloutDate: 'last_working_day', // Default to last working day
        specificSalaryDate: '', // Specific date when salaryRolloutDate is 'specific_date'
        statutoryDetails: {
          pan: '',
          tan: '',
          tracesUserId: '',
          gstNumber: ''
        },
        banks: [
          {
            bankName: '',
            bankAccountNumber: '',
            bankIfscNumber: '',
            bsrCode: '',
            branchId: '',
            accountHolderName: '',
            upiId: '',
            qrCode: ''
          }
        ],
        authorizedSignatures: [],
        creditBureauDetails: {
          transUnion: {
            memberCode: '',
            memberShortName: ''
          },
          crif: {
            memberCode: '',
            memberShortName: ''
          },
          equifax: {
            memberCode: '',
            memberShortName: ''
          },
          experian: {
            memberCode: '',
            memberShortName: ''
          }
        },
        assetClassification: []
      },
      modal1: false,
      modal2: false,
      disabled: true,
      states: [],
      branches: [],
      addressLine1: '',
      map: null,
      placesServiceData: null,
      selectedAddress: '',
      orgDataStorage: this.$storage.getUniversal('orgData'),
      // Logo upload related data
      isDragOver: false,
      logoCropDialog: false,
      selectedFile: null,
      selectedFileName: null,
      cropperImage: null,
      croppedPreview: null,
      previewImage: null,
      pendingCroppedImage: null,
      // Branch markers data
      branchMarkers: [],
      branchInfoWindows: [],
      bankQrCodeUploadingIndex: null
    }
  },
  computed: {
    getLogoImage() {
      if (this.orgData?.logo) {
        return `${process.env.VUE_APP_S3_BASE_URL + this.orgData?.logo}`
      }
      return null
    },
    getAddressLine1() {
      return this.getAddressLine1 ? this.getAddressLine1 : this.form.address_line_1
    }
  },
  methods: {
    // Drag and drop methods (similar to GenericBulkUpload)
    handleDragOver(e) {
      e.preventDefault()
      e.stopPropagation()
    },

    handleDragEnter(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = true
    },

    handleDragLeave(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = false
    },

    handleDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDragOver = false

      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.handleFileSelect({ target: { files: [files[0]] } })
      }
    },

    handleFileSelect(event) {
      const file = event.target.files ? event.target.files[0] : event

      if (file) {
        // Validate file type
        const isValidType =
          file.type.match('image.*') ||
          file.name.endsWith('.jpg') ||
          file.name.endsWith('.jpeg') ||
          file.name.endsWith('.png') ||
          file.name.endsWith('.gif')

        if (!isValidType) {
          this.$toast.error('Please select a valid image file')
          return
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          this.$toast.error('File size should be less than 5MB')
          return
        }

        this.selectedFile = file
        this.selectedFileName = file.name

        // Process the file for cropping
        this.processFile(file)
      }
    },

    removeFile() {
      this.selectedFile = null
      this.selectedFileName = null
      this.cropperImage = null
      this.croppedPreview = null
      this.previewImage = this.getLogoImage
      this.pendingCroppedImage = null
      if (this.$refs.fileInputForCropper) {
        this.$refs.fileInputForCropper.value = ''
      }
    },

    removePreviewImage() {
      // Clear the preview image and show the upload area
      this.previewImage = null
      this.pendingCroppedImage = null
      // Also clear the selected file if there is one
      this.selectedFile = null
      this.selectedFileName = null
      if (this.$refs.fileInputForCropper) {
        this.$refs.fileInputForCropper.value = ''
      }
    },

    triggerFileSelect() {
      this.$refs.fileInputForCropper.click()
    },
    onFileSelectedForCropper(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.handleFileSelect({ target: { files: [files[0]] } })
      }
    },
    processFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropperImage = e.target.result
        // Automatically open the cropping dialog
        this.logoCropDialog = true
      }
      reader.readAsDataURL(file)
    },

    // Cropper methods
    closeLogoCropDialog() {
      this.logoCropDialog = false
    },
    cancelCropper() {
      this.cropperImage = null
      this.croppedPreview = null
      this.logoCropDialog = false
    },
    onCropperChange({ canvas }) {
      // Generate preview of the cropped image
      if (canvas) {
        this.croppedPreview = canvas.toDataURL('image/png')
      }
    },
    applyCroppedImage() {
      if (this.croppedPreview) {
        // Store the cropped image as pending to be saved on update
        this.pendingCroppedImage = this.croppedPreview
        // Update the preview in the drag-and-drop area
        this.previewImage = this.croppedPreview
        // Close the dialog
        this.closeLogoCropDialog()
      } else {
        this.$toast.error('No image to apply')
      }
    },

    async handleImageUpload(event) {
      const file = event.target.files[0]
      const formData = new FormData()
      formData.append('attachments', file)
      try {
        // First, if there's an existing logo, update the org record to remove it
        if (this.orgData?.logo) {
          const clearPayload = {
            orgCode: this.orgCode,
            name: this.orgData?.name,
            logo: '' // Clear the logo field
          }
          await this.$axios.put('/org/info', clearPayload)
        }

        const response = await this.$axios.post('/org/logo', formData)

        if (response.data.success) {
          const isUpdate = await this.updateOrgLogo(response.data.url)
          if (isUpdate) {
            // Update the orgData immediately to reflect the new logo
            if (this.orgData) {
              this.orgData.logo = response.data.url.doc_physical_path
            }
            await this.getOrgConfig() // Refresh org data
            this.removeFile() // Clear the selected file
            this.$toast.success('Logo uploaded successfully')
          }
        } else {
          this.$toast.error('Failed to upload logo')
        }
      } catch (error) {
        console.error('Error uploading logo:', error)
        const errorMessage = error.response?.data?.message || 'Failed to upload logo'
        this.$toast.error(errorMessage)
      }
    },

    async updateOrgLogo(url) {
      const payload = {
        orgCode: this.orgCode,
        name: this.orgData?.name,
        logo: url.doc_physical_path
      }
      try {
        const response = await this.$axios.put('/org/info', payload)

        if (response.data.success) {
          // Update the orgData immediately to reflect the new logo
          if (this.orgData) {
            this.orgData.logo = url.doc_physical_path
          }
          return true
        } else {
          return false
        }
      } catch (error) {
        console.error('Error updating org logo.', error)
        this.$toast.error('Error updating organization logo')
        return false
      }
    },

    addBank() {
      this.form.banks.push({
        bankName: '',
        branchName: '',
        bankAccountNumber: '',
        confirmBankAccountNumber: '',
        showAccountNumber: false,
        bankIfscNumber: '',
        bsrCode: '',
        branchId: '',
        branchAddress: '',
        accountHolderName: '',
        upiId: '',
        upiEnabled: false,
        qrCode: '',
        city: '',
        state: '',
        district: '',
        verified: false,
        loadingIfsc: false
      })
    },
    removeBank(index) {
      if (this.form.banks.length > 1) {
        this.form.banks.splice(index, 1)
      }
    },
    clearBankDetails(index) {
      this.$set(this.form.banks[index], 'bankIfscNumber', '')
      this.$set(this.form.banks[index], 'bankName', '')
      this.$set(this.form.banks[index], 'branchName', '')
      this.$set(this.form.banks[index], 'bsrCode', '')
      this.$set(this.form.banks[index], 'branchAddress', '')
      this.$set(this.form.banks[index], 'city', '')
      this.$set(this.form.banks[index], 'state', '')
      this.$set(this.form.banks[index], 'district', '')
      this.$set(this.form.banks[index], 'upiEnabled', false)
      this.$set(this.form.banks[index], 'upiId', '')
      this.$set(this.form.banks[index], 'verified', false)
    },
    onIfscInput(index, val) {
      if (!val || val.trim() === '') {
        this.clearBankDetails(index)
        return
      }
      const cleanVal = String(val).toUpperCase().replace(/[^A-Z0-9]/g, '')
      this.$set(this.form.banks[index], 'bankIfscNumber', cleanVal)
      if (cleanVal.length === 11) {
        this.lookupIfsc(index)
      } else if (this.form.banks[index].verified) {
        // If modified away from 11 characters after being verified, reset populated fields
        this.$set(this.form.banks[index], 'bankName', '')
        this.$set(this.form.banks[index], 'branchName', '')
        this.$set(this.form.banks[index], 'bsrCode', '')
        this.$set(this.form.banks[index], 'branchAddress', '')
        this.$set(this.form.banks[index], 'city', '')
        this.$set(this.form.banks[index], 'state', '')
        this.$set(this.form.banks[index], 'district', '')
        this.$set(this.form.banks[index], 'upiEnabled', false)
        this.$set(this.form.banks[index], 'upiId', '')
        this.$set(this.form.banks[index], 'verified', false)
      }
    },
    async lookupIfsc(index) {
      const bank = this.form.banks[index]
      if (!bank || !bank.bankIfscNumber) {
        this.$toast.error('Please enter an IFSC code')
        return
      }

      const ifsc = bank.bankIfscNumber.trim().toUpperCase()
      if (ifsc.length !== 11) {
        this.$toast.error('IFSC code must be 11 characters')
        return
      }

      this.$set(this.form.banks[index], 'loadingIfsc', true)
      try {
        const response = await this.$axios.get(`/ifsc/${ifsc}`)
        if (response.data && response.data.success && response.data.data) {
          const d = response.data.data
          this.$set(this.form.banks[index], 'bankName', d.bank_name || '')
          this.$set(this.form.banks[index], 'branchName', d.branch || '')
          this.$set(this.form.banks[index], 'bsrCode', d.micr || d.bsrCode || '')
          this.$set(this.form.banks[index], 'city', d.city || '')
          this.$set(this.form.banks[index], 'state', d.state || '')
          this.$set(this.form.banks[index], 'district', d.district || '')
          this.$set(this.form.banks[index], 'upiEnabled', Boolean(d.upi))
          if (!d.upi) {
            this.$set(this.form.banks[index], 'upiId', '')
          }
          this.$set(this.form.banks[index], 'verified', true)

          // Auto construct address from API response
          const addressParts = [d.address, d.city, d.state].filter(Boolean)
          const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : d.address || ''
          this.$set(this.form.banks[index], 'branchAddress', fullAddress)

          this.$toast.success(`Bank details verified: ${d.bank_name || ''} (${d.branch || ''})`)
        } else {
          this.$toast.error(response.data?.message || 'Failed to fetch bank details for this IFSC')
        }
      } catch (err) {
        console.error('Error fetching IFSC details:', err)
        const msg =
          err.response?.data?.message ||
          'Invalid IFSC code or branch details not found'
        this.$toast.error(msg)
      } finally {
        this.$set(this.form.banks[index], 'loadingIfsc', false)
      }
    },
    getBankQrDisplayUrl(bank) {
      if (!bank || !bank.qrCode) return ''
      if (bank.qrCode.startsWith('http') || bank.qrCode.startsWith('data:')) {
        return bank.qrCode
      }
      return (process.env.VUE_APP_S3_BASE_URL || '') + bank.qrCode
    },
    triggerBankQrSelect(index) {
      const inputRef = this.$refs[`bankQrInput_${index}`]
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef
      if (input) input.click()
    },
    async handleBankQrCodeUpload(event, index) {
      const file = event.target?.files?.[0]
      if (!file) return
      this.bankQrCodeUploadingIndex = index
      try {
        const formData = new FormData()
        formData.append('attachments', file)
        formData.append('type', 'qrCode')

        const response = await this.$axios.post('/org/logo', formData)
        if (response.data && response.data.success) {
          const s3Path =
            response.data.url?.doc_physical_path || response.data.url?.Key || response.data.url
          this.$set(this.form.banks[index], 'qrCode', typeof s3Path === 'string' ? s3Path : '')
          this.$toast.success('Bank QR Code uploaded successfully')
        } else {
          this.$toast.error('Failed to upload QR code')
        }
      } catch (err) {
        console.error('Error uploading bank QR code:', err)
        this.$toast.error(err.response?.data?.message || 'Failed to upload QR code')
      } finally {
        this.bankQrCodeUploadingIndex = null
        const inputRef = this.$refs[`bankQrInput_${index}`]
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef
        if (input) input.value = ''
      }
    },
    removeBankQrCode(index) {
      this.$set(this.form.banks[index], 'qrCode', '')
      const inputRef = this.$refs[`bankQrInput_${index}`]
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef
      if (input) input.value = ''
    },

    async addGoogleMapsScript() {
      try {
        const scriptTag = document.querySelector('script[src*="maps.googleapis.com"]')
        if (!scriptTag) {
          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_MAP_KEY}&libraries=places&callback=Function.prototype`
          script.defer = true
          script.async = true
          script.onload = this.initMap // Initialize the map after the script is loaded
          document.head.appendChild(script)
        } else {
          setTimeout(() => {
            this.initMap()
          }, 1000)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async initMap() {
      try {
        // Check if map div exists
        if (!this.$refs.map) {
          console.error('Map div not found')
          return
        }

        const mapOptions = {
          center: { lat: 37.7749, lng: -122.4194 },
          zoom: 15
        }
        this.map = new window.google.maps.Map(this.$refs.map, mapOptions)

        if (this.form.location && this.form.location.lat && this.form.location.lng) {
          this.setMarker(this.form.location.lat, this.form.location.lng, this.form.address_line_1)
        }

        this.placesServiceData = new google.maps.places.PlacesService(document.createElement('div'))

        // Fetch and display all branch markers
        await this.fetchAndDisplayBranches()
      } catch (error) {
        console.error('An error occurred while initializing the map:', error)
      }
    },
    customFilter() {
      return true
    },

    setMarker(lat, lng, address) {
      if (this.marker) {
        this.marker.setMap(null)
      }

      this.marker = new google.maps.Marker({
        map: this.map,
        position: { lat, lng },
        title: address
      })

      const infoWindowContent = `<div class="dark-bg-custom p-2">${address}</div>`

      if (this.infoWindow) {
        this.infoWindow.close()
      }

      this.infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent
      })

      this.infoWindow.open(this.map, this.marker)

      this.map.setCenter({ lat, lng })

      this.form.lat = lat
      this.form.lng = lng
      this.form.address_line_1 = address
    },

    // Fetch all branches and display them on the map
    async fetchAndDisplayBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        const branches = response.data?.branches || []

        // Format branches for dropdown
        this.branches = branches.map((b) => ({
          label: b.branchName,
          value: b._id,
          ...b
        }))

        // Only render map markers if Google Maps SDK and map instance are initialized
        if (typeof window.google !== 'undefined' && window.google.maps && this.map) {
          // Clear existing markers and info windows
          this.clearBranchMarkers()

          // Create bounds to auto-adjust map zoom
          const bounds = new window.google.maps.LatLngBounds()

          // Add markers for each branch
          branches.forEach((branch) => {
            // Check if branch has geofencing data with location
            if (branch.geoFencingData && branch.geoFencingData.location) {
              const { lat, lng } = branch.geoFencingData.location
              const position = { lat, lng }

              // Create marker
              const marker = new window.google.maps.Marker({
                position,
                map: this.map,
                title: branch.branchName
              })

              // Create info window content with branch name and address
              let infoContent = `<div class="p-2">`
              infoContent += `<h3 class="font-bold">${branch.branchName}</h3>`

              if (branch.branchLocation) {
                const location = branch.branchLocation
                infoContent += `<p class="mt-1">${location.addressLine1 || ''}</p>`
                if (location.city || location.state) {
                  infoContent += `<p>${location.city || ''}${
                    location.city && location.state ? ', ' : ''
                  }${location.state || ''}</p>`
                }
                if (location.pinCode) {
                  infoContent += `<p>${location.pinCode}</p>`
                }
              }

              infoContent += `</div>`

              // Create info window
              const infoWindow = new window.google.maps.InfoWindow({
                content: infoContent
              })

              // Store references
              this.branchMarkers.push(marker)
              this.branchInfoWindows.push(infoWindow)

              // Extend bounds to include this marker
              bounds.extend(position)
            }
          })

          // Adjust map zoom and center based on all markers
          if (this.branchMarkers.length > 0) {
            this.map.fitBounds(bounds)

            // If there's only one marker, set a reasonable zoom level
            if (this.branchMarkers.length === 1) {
              window.google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
                if (this.map.getZoom() > 15) {
                  this.map.setZoom(15)
                }
              })
            }

            // Auto open info windows for all branches after map is adjusted
            setTimeout(() => {
              this.branchInfoWindows.forEach((infoWindow, index) => {
                const marker = this.branchMarkers[index]
                if (marker && infoWindow) {
                  infoWindow.open(this.map, marker)
                }
              })
            }, 1000) // Delay to ensure map is fully loaded
          }
        }
      } catch (error) {
        console.error('Error fetching branches:', error)
      }
    },

    // Clear all branch markers from the map
    clearBranchMarkers() {
      // Close all info windows
      if (this.branchInfoWindows && this.branchInfoWindows.length) {
        this.branchInfoWindows.forEach((iw) => {
          if (iw && iw.close) iw.close()
        })
      }

      // Remove all markers from the map
      if (this.branchMarkers && this.branchMarkers.length) {
        this.branchMarkers.forEach((marker) => {
          if (marker && marker.setMap) marker.setMap(null)
        })
      }

      // Clear arrays
      this.branchMarkers = []
      this.branchInfoWindows = []
    },

    async getStates() {
      try {
        const response = await this.$axios.get('/cualitywork/states')

        this.states = response.data?.states
      } catch (error) {
        console.log(error)
      }
    },
    handleAddressSelectedPlace(place) {
      if (place) {
        this.form.address_line_1 = place.formatted_address || ''
        if (place.city) this.form.city = place.city
        if (place.state) this.form.state = place.state
        if (place.pincode) this.form.pincode = place.pincode
        if (place.location) {
          const lat =
            typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat
          const lng =
            typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng
          if (lat && lng) {
            this.form.lat = lat
            this.form.lng = lng
            if (this.map) {
              this.setMarker(lat, lng, place.formatted_address)
            }
          }
        }
      }
    },
    handleBankBranchSelectedPlace(place, index) {
      if (place && this.form.banks && this.form.banks[index]) {
        this.$set(this.form.banks[index], 'branchAddress', place.formatted_address || '')
      }
    },
    async getOrgConfig() {
      try {
        const response = await this.$axios.get('/organizations')
        this.orgData = response?.data?.organizations[0] || {}

        const salaryCreditSource =
          this.orgData && this.orgData.salaryCreditSource
            ? this.orgData.salaryCreditSource
            : {
                bankName: '',
                accountNumber: '',
                branch: '',
                ifscCode: ''
              }

        const defaultBanks = [
          {
            bankName: '',
            bankAccountNumber: '',
            confirmBankAccountNumber: '',
            showAccountNumber: false,
            bankIfscNumber: '',
            bsrCode: '',
            branchId: '',
            branchAddress: '',
            accountHolderName: '',
            upiId: '',
            upiEnabled: false,
            qrCode: ''
          }
        ]

        let initialBanks = defaultBanks
        if (this.orgData?.banks && this.orgData.banks.length > 0) {
          initialBanks = this.orgData.banks.map((b) => ({
            ...b,
            confirmBankAccountNumber: b.confirmBankAccountNumber || b.bankAccountNumber || '',
            branchAddress: b.branchAddress || '',
            upiEnabled: Boolean(b.upiId || b.upiEnabled),
            showAccountNumber: false
          }))
        } else if (this.orgData?.chalanBankDetails && this.orgData.chalanBankDetails.length > 0) {
          initialBanks = this.orgData.chalanBankDetails.map((b) => ({
            ...b,
            confirmBankAccountNumber: b.confirmBankAccountNumber || b.bankAccountNumber || '',
            branchAddress: b.branchAddress || '',
            upiEnabled: Boolean(b.upiId || b.upiEnabled),
            showAccountNumber: false
          }))
        }

        this.form = {
          orgId: this.orgData ? this.orgData.id : '',

          code: this.orgData ? this.orgData?.code : '',
          name: this.orgData ? this.orgData.name : '',
          mobile: this.orgData ? this.orgData.mobile || '' : '',
          valid_from: this.orgData ? this.orgData.valid_from : '',
          valid_to: this.orgData ? this.orgData.valid_to : '',
          address_line_1: this.orgData?.organization_address?.address_line_1 || '',
          address_line_2: this.orgData?.organization_address?.address_line_2 || '',
          city: this.orgData?.organization_address?.city || '',
          state: this.orgData?.organization_address?.state || '',
          pincode: this.orgData?.organization_address?.pincode || '',
          financialStartMonth: this.orgData ? this.orgData.financialStartMonth || 3 : 3, // Default to April
          reimbursementDate: this.orgData ? this.orgData.reimbursementDate || 15 : 15, // Default to 15th
          salaryRolloutDate: this.orgData
            ? this.orgData.salaryRolloutDate || 'last_working_day'
            : 'last_working_day',
          specificSalaryDate: this.orgData ? this.orgData.specificSalaryDate || '' : '',
          salaryCreditSource: salaryCreditSource,
          statutoryDetails: {
            pan: this.orgData?.statutoryDetails?.pan || '',
            tan: this.orgData?.statutoryDetails?.tan || '',
            tracesUserId: this.orgData?.statutoryDetails?.tracesUserId || '',
            gstNumber: this.orgData?.statutoryDetails?.gstNumber || ''
          },
          banks: initialBanks,
          authorizedSignatures: this.form?.authorizedSignatures || [],
          creditBureauDetails: this.form?.creditBureauDetails || {
            transUnion: { memberCode: '', memberShortName: '' },
            crif: { memberCode: '', memberShortName: '' },
            equifax: { memberCode: '', memberShortName: '' },
            experian: { memberCode: '', memberShortName: '' }
          },
          location: {
            lat: this.orgData?.organization_address?.lat,
            lng: this.orgData?.organization_address?.lng
          }
        }
        // Set the initial preview image to the current logo
        this.previewImage = this.getLogoImage
      } catch (error) {
        console.log(error)
      }
    },
    async getOrgTdsDetails() {
      try {
        const response = await this.$axios.get('/orgSetup/org-tds-details/')
        if (response.data && response.data.data) {
          const orgTdsData = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data

          if (!orgTdsData) return

          const { orgDetails, financialDetails } = orgTdsData

          // Map orgDetails to statutoryDetails
          if (orgDetails) {
            this.$set(this.form.statutoryDetails, 'pan', orgDetails.panNumber || '')
            this.$set(this.form.statutoryDetails, 'tan', orgDetails.tanNumber || '')
            this.$set(this.form.statutoryDetails, 'tracesUserId', orgDetails.tracesLoginId || '')
            this.$set(this.form.statutoryDetails, 'gstNumber', orgDetails.gstNumber || '')
          }

          // Map banks
          let mappedBanks = []
          if (orgTdsData.banks && orgTdsData.banks.length > 0) {
            mappedBanks = orgTdsData.banks
          } else if (
            (orgTdsData.chalanBankDetails && orgTdsData.chalanBankDetails.length > 0) ||
            (orgTdsData.salaryBankDetails && orgTdsData.salaryBankDetails.length > 0)
          ) {
            mappedBanks = [
              ...(orgTdsData.chalanBankDetails || []),
              ...(orgTdsData.salaryBankDetails || [])
            ]
          }

          if (mappedBanks.length > 0) {
            this.$set(
              this.form,
              'banks',
              mappedBanks.map((b) => ({
                bankName: b.bankName || '',
                bankAccountNumber: b.bankAccountNumber || '',
                confirmBankAccountNumber: b.confirmBankAccountNumber || b.bankAccountNumber || '',
                showAccountNumber: false,
                bankIfscNumber: b.bankIfscNumber || '',
                bsrCode: b.bsrCode || '',
                branchId:
                  b.branchId && typeof b.branchId === 'object'
                    ? b.branchId._id
                    : b.branchId || '',
                branchAddress: b.branchAddress || '',
                accountHolderName: b.accountHolderName || '',
                upiId: b.upiId || '',
                upiEnabled: Boolean(b.upiId || b.upiEnabled),
                qrCode: b.qrCode || ''
              }))
            )
          }

          // Map authorized signatures
          if (orgTdsData.authorizedSignatures && Array.isArray(orgTdsData.authorizedSignatures)) {
            this.$set(this.form, 'authorizedSignatures', orgTdsData.authorizedSignatures)
          }

          // Map creditBureauDetails
          if (orgTdsData.creditBureauDetails) {
            this.$set(this.form, 'creditBureauDetails', {
              transUnion: {
                memberCode: orgTdsData.creditBureauDetails.transUnion?.memberCode || '',
                memberShortName: orgTdsData.creditBureauDetails.transUnion?.memberShortName || ''
              },
              crif: {
                memberCode: orgTdsData.creditBureauDetails.crif?.memberCode || '',
                memberShortName: orgTdsData.creditBureauDetails.crif?.memberShortName || ''
              },
              equifax: {
                memberCode: orgTdsData.creditBureauDetails.equifax?.memberCode || '',
                memberShortName: orgTdsData.creditBureauDetails.equifax?.memberShortName || ''
              },
              experian: {
                memberCode: orgTdsData.creditBureauDetails.experian?.memberCode || '',
                memberShortName: orgTdsData.creditBureauDetails.experian?.memberShortName || ''
              }
            })
          }

          // Map financialDetails
          if (financialDetails) {
            // Convert startMonth from string/number if needed.
            if (financialDetails.startMonth !== undefined && financialDetails.startMonth !== null) {
              const startMonth = Number(financialDetails.startMonth)
              if (!isNaN(startMonth)) {
                this.form.financialStartMonth = startMonth
              }
            }

            if (financialDetails.reimbursementDate) {
              this.form.reimbursementDate = Number(financialDetails.reimbursementDate)
            }

            if (financialDetails.salaryDate) {
              const salaryDate = financialDetails.salaryDate
              if (salaryDate === 'last_working_day') {
                this.form.salaryRolloutDate = 'last_working_day'
                this.form.specificSalaryDate = ''
              } else {
                // Check if it's a number (specific date)
                if (!isNaN(Number(salaryDate))) {
                  this.form.salaryRolloutDate = 'specific_date'
                  this.form.specificSalaryDate = Number(salaryDate)
                } else {
                  // Fallback
                  this.form.salaryRolloutDate = 'last_working_day'
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching org TDS details:', error)
      }
    },
    async saveOrgTdsDetails() {
      // Determine salaryDate
      let salaryDate = ''
      if (this.form.salaryRolloutDate === 'last_working_day') {
        salaryDate = 'last_working_day'
      } else if (this.form.salaryRolloutDate === 'specific_date') {
        salaryDate = String(this.form.specificSalaryDate)
      }

      // Sanitize banks to ensure branchId is not an empty string and UI fields are not sent
      const sanitizedBanks = (this.form.banks || []).map((b) => ({
        bankName: b.bankName || '',
        bankAccountNumber: b.bankAccountNumber || '',
        bankIfscNumber: b.bankIfscNumber || '',
        bsrCode: b.bsrCode || '',
        branchId: b.branchId && b.branchId !== '' ? b.branchId : null,
        branchAddress: b.branchAddress || '',
        accountHolderName: b.accountHolderName || '',
        upiId: b.upiId || '',
        qrCode: b.qrCode || '',
        isPrimary: b.isPrimary || false
      }))

      // Prepare payload based on the unified banks model
      const payload = {
        orgDetails: {
          panNumber: this.form.statutoryDetails?.pan || '',
          tanNumber: this.form.statutoryDetails?.tan || '',
          tracesLoginId: this.form.statutoryDetails?.tracesUserId || '',
          gstNumber: this.form.statutoryDetails?.gstNumber || ''
        },
        banks: sanitizedBanks,
        authorizedSignatures: this.form.authorizedSignatures || [],
        creditBureauDetails: this.form.creditBureauDetails,
        financialDetails: {
          startMonth: String(this.form.financialStartMonth || '4'),
          reimbursementDate: String(this.form.reimbursementDate || '1'),
          salaryDate: salaryDate
        }
      }

      try {
        const response = await this.$axios.post('/orgSetup/org-tds-details/', payload)
        if (!response.data || !response.data.success) {
          console.warn('Failed to save TDS details via new API', response)
        }
      } catch (error) {
        console.error('Error saving org TDS details:', error)
        this.$toast.error('Error saving TDS configuration')
      }
    },
    onSignaturesUpdated(updatedSignatures) {
      this.$set(this.form, 'authorizedSignatures', updatedSignatures)
    },
    getMediaUrl(url) {
      if (!url) return ''
      if (typeof url === 'object') {
        url = url.url || url.location || ''
      }
      if (typeof url !== 'string') return ''
      if (url.startsWith('http') || url.startsWith('data:image')) {
        return url
      }
      return `${process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'}${url.replace(
        /^\//,
        ''
      )}`
    },
    async updateOrg() {
      if (this.form.code == '' || this.form.code == undefined) {
        this.toasterError('Please enter the Organization code')
        return false
      }
      if (this.form.code.length != 6) {
        this.toasterError('Organization code should be 6 characters')
        return false
      }
      if (this.form.name == '' || this.form.name == undefined) {
        this.toasterError('Please enter the Organization name')
        return false
      }

      // Validate account numbers match for all bank accounts
      for (let i = 0; i < this.form.banks.length; i++) {
        const b = this.form.banks[i]
        if (
          b.bankAccountNumber &&
          b.confirmBankAccountNumber &&
          b.bankAccountNumber !== b.confirmBankAccountNumber
        ) {
          this.toasterError(
            `Account Number and Confirm Account Number do not match for Bank Account ${i + 1}`
          )
          return false
        }
      }

      // If there's a pending cropped image, save it
      if (this.pendingCroppedImage) {
        try {
          // Convert data URL to blob
          const blob = await fetch(this.pendingCroppedImage).then((res) => res.blob())
          const croppedFile = new File([blob], 'cropped-logo.png', { type: 'image/png' })

          // Create a mock event object to pass to handleImageUpload
          const mockEvent = {
            target: {
              files: [croppedFile]
            }
          }

          // Upload the cropped image
          await this.handleImageUpload(mockEvent)

          // Clear the pending image
          this.pendingCroppedImage = null
        } catch (error) {
          console.error('Error saving cropped image:', error)
          this.$toast.error('Error saving logo')
          return
        }
      }

      const sanitizedBanks = (this.form.banks || []).map((b) => ({
        bankName: b.bankName || '',
        bankAccountNumber: b.bankAccountNumber || '',
        bankIfscNumber: b.bankIfscNumber || '',
        bsrCode: b.bsrCode || '',
        branchId: b.branchId && b.branchId !== '' ? b.branchId : null,
        branchAddress: b.branchAddress || '',
        accountHolderName: b.accountHolderName || '',
        upiId: b.upiId || '',
        qrCode: b.qrCode || '',
        isPrimary: b.isPrimary || false
      }))

      const payload = {
        orgCode: this.form.code,
        name: this.form.name,
        mobile: this.form.mobile,
        financialStartMonth: Number(this.form.financialStartMonth),
        reimbursementDate: Number(this.form.reimbursementDate),
        salaryRolloutDate: this.form.salaryRolloutDate,
        specificSalaryDate: this.form.specificSalaryDate
          ? Number(this.form.specificSalaryDate)
          : undefined,
        salaryCreditSource: sanitizedBanks[0] || {},
        address: {
          address_line_1: this.form.address_line_1,
          address_line_2: this.form.address_line_2,
          city: this.form.city,
          state: this.form.state,
          pincode: Number(this.form.pincode),
          lat: Number(this.form.lat),
          lng: Number(this.form.lng)
        },
        statutoryDetails: this.form.statutoryDetails,
        banks: sanitizedBanks
      }
      try {
        const response = await this.$axios.put(`/org/info`, payload)
        if (response.data.success) {
          // this.orgDataStorage.name = response.data.orgInfoUpdated.name;
          if (this.orgDataStorage) {
            this.orgDataStorage.name = response.data.orgInfoUpdated.name
            this.orgDataStorage.mobile = response.data.orgInfoUpdated.mobile
            if (this.orgDataStorage.organization_address) {
              this.orgDataStorage.organization_address.address_line_1 =
                response.data.orgInfoUpdated.address_line_1
              this.orgDataStorage.organization_address.city = response.data.orgInfoUpdated.city
              this.orgDataStorage.organization_address.lat = response.data.orgInfoUpdated.lat
              this.orgDataStorage.organization_address.lng = response.data.orgInfoUpdated.lng
              this.orgDataStorage.organization_address.state = response.data.orgInfoUpdated.state
              this.orgDataStorage.organization_address.pincode = response.data.orgInfoUpdated.pincode
            }
            this.setCookie('orgData', JSON.stringify(this.orgDataStorage))
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem('orgData', JSON.stringify(this.orgDataStorage))
            }
          }

          // Save TDS details via new API
          await this.saveOrgTdsDetails()

          const successMessage = response?.data?.message || 'Organization updated successfully'
          this.$toast.success(successMessage)
        }
      } catch (error) {
        console.error('Error updating org.', error)
      }
    },
    toasterError(msg) {
      this.$toast.error(msg)
    },
    setCookie(name, value) {
      document.cookie = name + '=' + (value || '') + '; path=/'
    },
    async getDomainStatus() {
      this.domainLoading = true
      try {
        const response = await this.$axios.get('/orgSetup/org-domain-management/status')
        if (response.data && response.data.success) {
          this.domainConfig = {
            configured: response.data.configured || false,
            domain: response.data.domain || '',
            domainStatus: response.data.domainStatus || '',
            dkimStatus: response.data.dkimStatus || '',
            fullyVerified: response.data.fullyVerified || false,
            dnsRecords: response.data.dnsRecords || [],
            verificationRequestedAt: response.data.verificationRequestedAt || null,
            verifiedAt: response.data.verifiedAt || null,
            note: response.data.note || '',
            instructions: response.data.instructions || null
          }
          if (this.domainConfig.configured) {
            this.domainName = this.domainConfig.domain
          }
        }
      } catch (error) {
        console.error('Error fetching domain status:', error)
        this.$toast.error('Failed to retrieve domain configuration status')
      } finally {
        this.domainLoading = false
      }
    },
    async registerDomain() {
      if (!this.domainName) {
        this.$toast.error('Please enter a domain name')
        return
      }
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/
      if (!domainRegex.test(this.domainName)) {
        this.$toast.error('Invalid domain format. Example: company.com')
        return
      }
      this.domainActionLoading = true
      try {
        const response = await this.$axios.post('/orgSetup/org-domain-management/verify', {
          domain: this.domainName
        })
        if (response.data && response.data.success) {
          this.$toast.success('Domain registration initiated successfully')
          await this.getDomainStatus()
        }
      } catch (error) {
        console.error('Error registering domain:', error)
        const msg = error.response?.data?.message || 'Failed to register domain'
        this.$toast.error(msg)
      } finally {
        this.domainActionLoading = false
      }
    },
    async removeDomain() {
      if (
        !confirm(
          'Are you sure you want to remove the domain configuration? This will delete the domain setup and stop all emails sent from this domain.'
        )
      ) {
        return
      }
      this.domainActionLoading = true
      try {
        const response = await this.$axios.delete('/orgSetup/org-domain-management/remove')
        if (response.data && response.data.success) {
          this.$toast.success('Domain configuration removed successfully')
          this.domainName = ''
          this.domainConfig = {
            configured: false,
            domain: '',
            domainStatus: '',
            dkimStatus: '',
            fullyVerified: false,
            dnsRecords: [],
            verificationRequestedAt: null,
            verifiedAt: null,
            note: '',
            instructions: null
          }
        }
      } catch (error) {
        console.error('Error removing domain:', error)
        const msg = error.response?.data?.message || 'Failed to remove domain configuration'
        this.$toast.error(msg)
      } finally {
        this.domainActionLoading = false
      }
    },
    copyToClipboard(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(
        () => {
          this.$toast.success('Copied to clipboard!')
        },
        () => {
          this.$toast.error('Failed to copy to clipboard')
        }
      )
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  mounted() {
    const orgData = this.$storage.getUniversal('orgData')
    const addressLine_1 = orgData?.organization_address?.address_line_1

    // Initialize branches with just the main address initially, but fetchAndDisplayBranches will overwrite/append
    if (addressLine_1) {
      this.branches = [{ label: addressLine_1, value: addressLine_1, _id: addressLine_1 }]
      this.addressLine1 = addressLine_1
    }
    this.fetchAndDisplayBranches()
    this.addGoogleMapsScript()
    this.getOrgConfig().then(() => {
      this.getOrgTdsDetails()
    })
    this.getStates()
    this.getDomainStatus()
  }
}
</script>
<style></style>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Alex+Brush&family=Allura&family=Caveat:wght@600&family=Cedarville+Cursive&family=Dancing+Script:wght@600&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&display=swap');

.map-container {
  border: 1px solid #ccc !important;
  padding: 10px !important;
}

.logo-upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  min-height: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.logo-upload-area.drag-over {
  border-color: #2196f3;
  background-color: rgba(33, 150, 243, 0.1);
}

.logo-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px;
}

/* .logo-wrapper {
  margin-bottom: 16px;
  position: relative;
} */

.org-logo {
  max-width: 200px;
  max-height: 100px;
  object-fit: contain;
}

.close-icon {
  position: absolute;
  top: -10px;
  right: -10px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  color: #ff5252;
  font-size: 24px;
}

.upload-placeholder {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 160px;
}

.logo-upload-container {
  min-height: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cropper-example {
  height: 400px;
  width: 100%;
  background: #ddd;
}

.cropper-container {
  max-width: 100%;
  margin: 0 auto;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 0px !important;
  color: #21427d;
  margin: 0;
}

.tab-items {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
